import React, { useState, useEffect, useRef, useCallback } from 'react';
import FrameworkViz from './frameworkvisualizer';
import Papa from 'papaparse';
import HVHSPage from './hvhs';
import OECDExplorer from './OECDExplorer';
import HVHSViz from './hvhsvisualizer';
import Plotly from 'plotly.js-dist';

// Landing feature cards — one shell shared by both cards so they cannot drift.
const LANDING_CARD_MAP_HEIGHT = 300;
const LANDING_CARD_SHADOW = '0 2px 4px rgba(0,0,0,0.04)';
const LANDING_CARD_SHADOW_HOVER = '0 12px 24px rgba(0,0,0,0.12)';
const landingCardStyles = {
  card: {
    flex: '1 1 0',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: LANDING_CARD_SHADOW
  },
  header: {
    padding: '1.5rem',
    borderBottom: '1px solid #e5e7eb',
    minHeight: '112px',
    boxSizing: 'border-box'
  },
  title: {
    fontSize: '1.25rem',
    lineHeight: '1.75rem',
    fontWeight: '600',
    color: '#1F2937',
    margin: '0 0 0.5rem 0',
    textAlign: 'left'
  },
  description: {
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    color: '#6B7280',
    margin: 0,
    textAlign: 'left'
  },
  mapWrap: {
    padding: '1rem',
    height: `${LANDING_CARD_MAP_HEIGHT + 32}px`,
    boxSizing: 'border-box'
  },
  mapViewport: {
    position: 'relative',
    width: '100%',
    height: `${LANDING_CARD_MAP_HEIGHT}px`,
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid #E5E7EB'
  },
  cta: {
    marginTop: 'auto',
    padding: '0 1.5rem',
    height: '56px',
    boxSizing: 'border-box',
    backgroundColor: '#F9FAFB',
    borderTop: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    transition: 'all 0.3s ease'
  },
  ctaButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    height: '100%',
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#113743',
    fontFamily: 'inherit',
    textAlign: 'left'
  }
};
// One geo/layout for BOTH landing previews so the globes share projection, scale,
// centring and margins (reviewer: maps sat at different heights within their cards).
// Titles/legends live in HTML overlays (previewChip), never inside the plot.
// 19 Oct feedback: orthographic globe, rotated so Africa/Europe/Asia face the viewer.
// scale 0.92 keeps the whole disc inside the 300px viewport with a small margin.
const LANDING_PREVIEW_BG = '#1e3a5f';
const LANDING_PREVIEW_OCEAN = '#2a4d78';
const LANDING_PREVIEW_ROTATION = { lon: 20, lat: 20, roll: 0 };
const LANDING_PREVIEW_GLOBE_SCALE = 0.92;
// Red→green score ramp shared by both previews. Derived from HVHS_SCORE_BANDS
// (palette contract, 19 Oct spec); the fallback mirrors the same hexes.
const LANDING_SCORE_RAMP_FALLBACK = ['#D73027', '#FC8D59', '#FEE08B', '#91CF60', '#1A9850'];
const landingScoreRampColors = () => (
  (HVHSViz.HVHS_SCORE_BANDS && HVHSViz.HVHS_SCORE_BANDS.length)
    ? HVHSViz.HVHS_SCORE_BANDS.map(b => b.color)
    : LANDING_SCORE_RAMP_FALLBACK
);
// Stepped (5 flat bands) — matches the full HVHS map.
const landingSteppedColorscale = () => {
  const colors = landingScoreRampColors();
  return colors.flatMap((c, i, arr) => [[i / arr.length, c], [(i + 1) / arr.length, c]]);
};
// Continuous — same hexes as evenly spaced stops, for the life-expectancy preview.
const landingContinuousColorscale = () => {
  const colors = landingScoreRampColors();
  return colors.map((c, i, arr) => [i / (arr.length - 1), c]);
};
// 'globe' (orthographic) for the HVHS card; 'flat' (natural earth) for the
// Health Systems Visualizer card, which keeps its original flat preview.
const landingPreviewLayout = (projection = 'globe') => ({
  geo: {
    scope: 'world',
    ...(projection === 'globe'
      ? {
          projection: { type: 'orthographic', rotation: { ...LANDING_PREVIEW_ROTATION }, scale: LANDING_PREVIEW_GLOBE_SCALE },
          // Frame = the globe's outline; ocean a shade lighter than the paper so the disc reads as a globe.
          showframe: true, framecolor: '#D6D6DA', framewidth: 1,
          showocean: true, oceancolor: LANDING_PREVIEW_OCEAN
        }
      : {
          projection: { type: 'natural earth', scale: 1.15 },
          center: { lon: 20, lat: 10 },
          showframe: false,
          showocean: true, oceancolor: LANDING_PREVIEW_BG
        }),
    showcoastlines: true, coastlinecolor: '#D6D6DA',
    showland: true, landcolor: '#E5E7EB',
    showlakes: false,
    showcountries: true, countrycolor: '#D6D6DA', countrywidth: 0.5,
    bgcolor: LANDING_PREVIEW_BG
  },
  margin: { l: 0, r: 0, t: 0, b: 0 },
  paper_bgcolor: LANDING_PREVIEW_BG,
  plot_bgcolor: LANDING_PREVIEW_BG,
  height: LANDING_CARD_MAP_HEIGHT,
  autosize: true,
  dragmode: false,
  showlegend: false
});
const previewChip = (corner) => ({
  position: 'absolute',
  top: '8px',
  [corner]: '8px',
  padding: '4px 8px',
  borderRadius: '4px',
  backgroundColor: 'rgba(255,255,255,0.9)',
  fontSize: '10px',
  lineHeight: '14px',
  color: '#1F2937',
  fontFamily: 'Arial, sans-serif',
  pointerEvents: 'none'
});
const liftLandingCard = (e) => {
  e.currentTarget.style.transform = 'translateY(-4px)';
  e.currentTarget.style.boxShadow = LANDING_CARD_SHADOW_HOVER;
};
const restLandingCard = (e) => {
  e.currentTarget.style.transform = 'translateY(0)';
  e.currentTarget.style.boxShadow = LANDING_CARD_SHADOW;
};

// Animated World Map Mini-Visualization using Plotly Choropleth (adapted from CW_VIZ)
const AnimatedWorldMap = () => {
  const [currentYearIndex, setCurrentYearIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const mapRef = useRef(null);
  const intervalRef = useRef(null);

  const years = [2000, 2005, 2010, 2015, 2020, 2023];

  // G20+ countries with life expectancy at birth data by year
  const countryData = [
    { iso: 'USA', name: 'United States', values: [76.6, 77.6, 78.5, 78.7, 77.0, 77.5] },
    { iso: 'GBR', name: 'United Kingdom', values: [77.7, 79.0, 80.4, 81.0, 80.3, 81.2] },
    { iso: 'DEU', name: 'Germany', values: [78.0, 79.3, 80.0, 80.6, 80.9, 80.6] },
    { iso: 'FRA', name: 'France', values: [79.0, 80.2, 81.7, 82.3, 82.0, 82.5] },
    { iso: 'JPN', name: 'Japan', values: [81.1, 82.0, 83.0, 83.8, 84.6, 84.8] },
    { iso: 'CAN', name: 'Canada', values: [79.2, 80.1, 81.3, 81.8, 81.5, 81.6] },
    { iso: 'AUS', name: 'Australia', values: [79.2, 80.8, 81.7, 82.4, 83.2, 83.1] },
    { iso: 'ITA', name: 'Italy', values: [79.8, 80.9, 82.0, 82.7, 82.3, 83.1] },
    { iso: 'KOR', name: 'South Korea', values: [75.9, 78.6, 80.2, 82.1, 83.5, 83.6] },
    { iso: 'BRA', name: 'Brazil', values: [70.3, 72.0, 73.4, 75.0, 75.3, 75.4] },
    { iso: 'IND', name: 'India', values: [62.7, 64.9, 67.2, 69.3, 70.2, 72.0] },
    { iso: 'CHN', name: 'China', values: [71.7, 74.0, 75.0, 76.1, 77.1, 78.2] },
    { iso: 'RUS', name: 'Russia', values: [65.5, 65.4, 68.9, 71.4, 71.3, 72.7] },
    { iso: 'ZAF', name: 'South Africa', values: [58.4, 53.9, 58.9, 64.1, 65.2, 66.1] },
    { iso: 'MEX', name: 'Mexico', values: [74.4, 75.4, 76.7, 75.0, 75.1, 75.5] },
    { iso: 'IDN', name: 'Indonesia', values: [66.3, 67.4, 69.3, 71.0, 71.7, 72.4] },
    { iso: 'TUR', name: 'Turkey', values: [70.0, 73.0, 74.5, 75.8, 76.4, 76.0] },
    { iso: 'SAU', name: 'Saudi Arabia', values: [72.2, 73.4, 74.3, 74.9, 75.7, 77.6] },
    { iso: 'ARG', name: 'Argentina', values: [74.1, 75.2, 76.0, 76.5, 76.5, 76.6] },
    { iso: 'ESP', name: 'Spain', values: [79.3, 80.3, 82.3, 83.0, 82.4, 83.1] },
    { iso: 'NGA', name: 'Nigeria', values: [47.1, 49.5, 51.3, 51.9, 53.1, 54.5] },
    { iso: 'THA', name: 'Thailand', values: [70.6, 73.3, 74.2, 75.5, 77.7, 78.7] },
    { iso: 'EGY', name: 'Egypt', values: [68.6, 69.6, 70.3, 71.1, 71.5, 72.5] },
    { iso: 'PAK', name: 'Pakistan', values: [61.9, 62.4, 64.4, 65.6, 65.7, 67.6] },
    { iso: 'BGD', name: 'Bangladesh', values: [62.0, 65.5, 68.0, 70.5, 71.4, 74.7] },
    { iso: 'NZL', name: 'New Zealand', values: [78.6, 79.9, 80.9, 81.6, 82.3, 83.0] },
    { iso: 'NOR', name: 'Norway', values: [78.7, 80.3, 81.2, 82.3, 83.2, 83.3] },
    { iso: 'SWE', name: 'Sweden', values: [79.7, 80.6, 81.5, 82.3, 82.4, 83.1] },
    { iso: 'CHE', name: 'Switzerland', values: [79.9, 81.4, 82.6, 83.1, 83.1, 83.4] },
    { iso: 'SGP', name: 'Singapore', values: [78.0, 80.0, 81.5, 82.7, 83.5, 82.9] },
    { iso: 'ISR', name: 'Israel', values: [78.9, 80.3, 81.7, 82.1, 82.6, 82.6] },
  ];

  // Animation effect
  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrentYearIndex(prev => (prev + 1) % years.length);
      }, 1500);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying, years.length]);

  // Render Plotly map
  useEffect(() => {
    if (!mapRef.current) return;

    const locations = countryData.map(c => c.iso);
    const values = countryData.map(c => c.values[currentYearIndex]);
    const texts = countryData.map(c => `${c.name}: ${c.values[currentYearIndex].toFixed(1)} years`);

    const minVal = 45;
    const maxVal = 85;
    const normalizedValues = values.map(v => ((v - minVal) / (maxVal - minVal)) * 100);

    const data = [{
      type: 'choropleth',
      locations: locations,
      z: normalizedValues,
      text: texts,
      colorscale: landingContinuousColorscale(),
      zmin: 0,
      zmax: 100,
      marker: {
        line: { color: '#ffffff', width: 0.5 }
      },
      showscale: false,
      hoverinfo: 'text'
    }];

    const layout = landingPreviewLayout('flat');

    Plotly.react(mapRef.current, data, layout, {
      displayModeBar: false,
      responsive: true,
      staticPlot: true
    });
  }, [currentYearIndex, years]);

  return (
    <div
      style={{ ...landingCardStyles.mapViewport, backgroundColor: LANDING_PREVIEW_BG }}
      onMouseEnter={() => setIsPlaying(false)}
      onMouseLeave={() => setIsPlaying(true)}
    >
      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
      <div style={previewChip('left')}>Life Expectancy at Birth (Years)</div>
      <div style={{ ...previewChip('right'), fontSize: '12px', fontWeight: 700, color: '#113743' }}>
        {years[currentYearIndex]}
      </div>
      <div style={{
        position: 'absolute',
        bottom: '8px',
        right: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        backgroundColor: 'rgba(255,255,255,0.9)',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '10px',
        color: '#6B7280'
      }}>
        <div style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: isPlaying ? '#10B981' : '#F59E0B'
        }} />
        {isPlaying ? 'Playing' : 'Paused'}
      </div>
    </div>
  );
};

// HVHS Overall-Score world-map preview for the "Explore HVHS" card.
// Replaces the India 2025 radial preview per CR feedback (27 Apr).
const HVHSMapPreview = () => {
  const chartRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!chartRef.current || loaded) return;

    const loadAndRender = async () => {
      try {
        const allData = await HVHSViz.loadAllCountriesData();

        // Build country → ISO3 + Overall Score
        const countryScoresMap = HVHSViz.getAllCountryScores
          ? HVHSViz.getAllCountryScores(allData)
          : (() => {
              const out = {};
              const countries = HVHSViz.getAvailableCountries(allData);
              countries.forEach(c => {
                const cd = HVHSViz.filterDataByCountry(allData, c);
                const ov = cd.find(r => r.Variable === 'Overall');
                if (ov && ov.Value != null && ov.Value !== '') out[c] = parseFloat(ov.Value);
              });
              return out;
            })();

        const countryToISO3 = {};
        allData.forEach(row => {
          if (row.Country && row['ISO-Code']) countryToISO3[row.Country] = row['ISO-Code'];
        });

        const validData = Object.keys(countryScoresMap)
          .map(c => ({ country: c, code: countryToISO3[c] || c, score: countryScoresMap[c] }))
          .filter(d => d.code && d.code.length === 3 && Number.isFinite(d.score));

        const data = [{
          type: 'choropleth',
          locations: validData.map(d => d.code),
          z: validData.map(d => d.score),
          text: validData.map(d => d.country),
          customdata: validData.map(d => d.score),
          hovertemplate: '<b>%{text}</b><br>Overall Score: %{customdata:.1f} / 5.0<extra></extra>',
          // Same five bands as the full HVHS map; the card chip replaces the colourbar.
          colorscale: landingSteppedColorscale(),
          showscale: false,
          zmin: 0, zmax: 5,
          marker: { line: { color: '#D6D6DA', width: 0.4 } }
        }];

        const layout = landingPreviewLayout();

        Plotly.newPlot(chartRef.current, data, layout, {
          responsive: true,
          displayModeBar: false,
          displaylogo: false
        });
        setLoaded(true);
      } catch (err) {
        console.error('HVHSMapPreview error:', err);
      }
    };

    loadAndRender();
  }, [loaded]);

  return (
    <div style={{ ...landingCardStyles.mapViewport, backgroundColor: LANDING_PREVIEW_BG }}>
      <div ref={chartRef} style={{ width: '100%', height: '100%' }} />
      <div style={previewChip('left')}>Overall high value health system score (0–5)</div>
    </div>
  );
};

// Framework details pop-over (19 Oct feedback). Replaces inline explanatory copy on
// the landing page. Accessible: labelled ⓘ button, dialog role, Escape/backdrop close,
// focus moves to the dialog on open and returns to the trigger on close.
const FRAMEWORK_INFO_TEXT = 'The Harvard high value health systems model consists of 10 interdependent and mutually reinforcing design components that characterize the ongoing transition in health systems towards a \u2018value\u2019 predominant system orientation. The framework assesses health systems across these components \u2014 including governance, financing, service delivery, health workforce, information systems, and more \u2014 to provide a holistic view of health system performance and identify opportunities for improvement.';

const FrameworkInfoButton = () => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const closeRef = useRef(null);

  const close = useCallback(() => {
    setOpen(false);
    if (triggerRef.current) triggerRef.current.focus();
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
      }
    };
    document.addEventListener('keydown', onKey);
    if (closeRef.current) closeRef.current.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label="About the framework"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '24px',
          height: '24px',
          padding: 0,
          border: '1px solid #9CA3AF',
          borderRadius: '50%',
          background: 'white',
          color: '#113743',
          fontSize: '0.8125rem',
          fontWeight: '700',
          fontFamily: 'Georgia, serif',
          lineHeight: 1,
          cursor: 'pointer',
          verticalAlign: 'middle',
          flexShrink: 0
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#113743'; e.currentTarget.style.backgroundColor = '#F3F4F6'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#9CA3AF'; e.currentTarget.style.backgroundColor = 'white'; }}
      >
        <span aria-hidden="true">i</span>
      </button>

      {open && (
        <div
          onClick={close}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(17, 24, 39, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3000,
            padding: '1rem'
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="framework-info-title"
            aria-describedby="framework-info-body"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 12px 24px rgba(0,0,0,0.18)',
              maxWidth: '560px',
              width: '100%',
              padding: '1.5rem',
              boxSizing: 'border-box',
              textAlign: 'left'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
              <h2 id="framework-info-title" style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1F2937', margin: 0, textAlign: 'left' }}>
                About the framework
              </h2>
              <button
                ref={closeRef}
                type="button"
                aria-label="Close"
                onClick={close}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#6B7280',
                  fontSize: '1.25rem',
                  lineHeight: 1,
                  cursor: 'pointer',
                  padding: '0.25rem',
                  flexShrink: 0
                }}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <p id="framework-info-body" style={{ fontSize: '0.9375rem', lineHeight: '1.6', color: '#374151', margin: '0.75rem 0 0 0', textAlign: 'left' }}>
              {FRAMEWORK_INFO_TEXT}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

const LandingPage = ({ 
  onNavigateToVisualizer, 
  previewData,
  onSearch 
}) => {
  console.log('🎬 LandingPage component rendered');
  
  const [searchQuery, setSearchQuery] = useState('');
  
  // Autocomplete states
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  const [variableList, setVariableList] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  // Metadata states
  const [metadata, setMetadata] = useState([]);
  const [showMetadata, setShowMetadata] = useState(false);
  const [selectedVariableMetadata, setSelectedVariableMetadata] = useState(null);
  
  // Refs
  const searchInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  
  // Framework visualizer state
  const [showFramework, setShowFramework] = useState(false);
  const [showHVHS, setShowHVHS] = useState(false);
  const [hvhsView, setHvhsView] = useState('map'); // 'map' | 'profile' | 'benchmarking' | 'about'
  const [showHVHSDropdown, setShowHVHSDropdown] = useState(false);
  const [showOECDExplorer, setShowOECDExplorer] = useState(false);
  const [showNCCP, setShowNCCP] = useState(false); // NCCP v2 demo tab (iframe → proxied Flask app)
  const hvhsDropdownRef = useRef(null);
  const openOECDExplorerFromCard = () => { setShowNCCP(false); setShowOECDExplorer(true); setShowHVHS(false); setShowFramework(false); };
  const openHVHSFromCard = () => { setShowNCCP(false); setShowFramework(false); setShowOECDExplorer(false); setShowHVHS(true); };
  const [showWorldMap, setShowWorldMap] = useState(true);
  const [allCountriesData, setAllCountriesData] = useState([]);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [parsedData, setParsedData] = useState(null);
  const [averagedScores, setAveragedScores] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [loadingFramework, setLoadingFramework] = useState(false);
  const [countryData, setCountryData] = useState([]);
  const [sectionDetailedData, setSectionDetailedData] = useState(null);
  
  const [pendingCountrySelection, setPendingCountrySelection] = useState(null);
  
  console.log('🔍 Component state:', { showFramework, showHVHS, showWorldMap, dataLength: allCountriesData.length });
  
  // Refs for Plotly charts
  const worldMapRef = useRef(null);
  const radialChartRef = useRef(null);
  const distributionChartRef = useRef(null);

  // Load variable and country lists on mount
  useEffect(() => {
    const loadLists = async () => {
      try {
        // Load variable list
        const varResponse = await fetch('/Current/variable_list.txt');
        if (varResponse.ok) {
          const varText = await varResponse.text();
          const variables = varText.split('\n')
            .map(v => v.trim())
            .filter(v => v && v.length > 0);
          setVariableList(variables);
          console.log('✅ Loaded variables:', variables.length);
        }
        
        // Load country list
        const countryResponse = await fetch('/Current/country_list.txt');
        if (countryResponse.ok) {
          const countryText = await countryResponse.text();
          const countries = countryText.split('\n')
            .map(c => c.trim())
            .filter(c => c && c.length > 0);
          setCountryList(countries);
          console.log('✅ Loaded countries:', countries.length);
        }
        
        // Load metadata
        const metadataResponse = await fetch('/Current/MetaData.csv');
        if (metadataResponse.ok) {
          const metadataText = await metadataResponse.text();
          Papa.parse(metadataText, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
              const cleanedMetadata = results.data.filter(row => 
                row && row.Variable && typeof row.Variable === 'string' && row.Variable.trim()
              );
              setMetadata(cleanedMetadata);
              console.log('✅ Loaded metadata:', cleanedMetadata.length);
            }
          });
        }
      } catch (error) {
        console.error('Error loading lists:', error);
      }
    };
    
    loadLists();
  }, []);

  // Filter options as user types
  useEffect(() => {
    if (!searchQuery.trim()) {
      setAutocompleteOptions([]);
      setShowAutocomplete(false);
      return;
    }

    const query = searchQuery.toLowerCase();
    const maxResults = 10;
    
    // Filter variables
    const matchedVariables = variableList
      .filter(v => v.toLowerCase().includes(query))
      .slice(0, maxResults)
      .map(v => ({ type: 'variable', value: v }));
    
    // Filter countries
    const matchedCountries = countryList
      .filter(c => c.toLowerCase().includes(query))
      .slice(0, maxResults)
      .map(c => ({ type: 'country', value: c }));
    
    // Combine results with variables first
    const combined = [...matchedVariables, ...matchedCountries].slice(0, maxResults);
    
    setAutocompleteOptions(combined);
    setShowAutocomplete(combined.length > 0);
    setSelectedIndex(-1);
  }, [searchQuery, variableList, countryList]);

  // Handle click outside to close autocomplete
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        autocompleteRef.current && 
        !autocompleteRef.current.contains(event.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowAutocomplete(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close HVHS dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (hvhsDropdownRef.current && !hvhsDropdownRef.current.contains(event.target)) {
        setShowHVHSDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle selection from autocomplete
  const handleSelectOption = (option) => {
    setSearchQuery(option.value);
    setShowAutocomplete(false);
    
    if (option.type === 'variable') {
      // Show metadata for selected variable
      const variableMetadata = metadata.find(m => 
        m.Variable && m.Variable.trim() === option.value.trim()
      );
      
      if (variableMetadata) {
        setSelectedVariableMetadata(variableMetadata);
        setShowMetadata(true);
      } else {
        // If no metadata found, still show a popup with basic info
        setSelectedVariableMetadata({
          Variable: option.value,
          'Variable Definition': 'Metadata not available for this variable'
        });
        setShowMetadata(true);
      }
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showAutocomplete) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => 
        prev < autocompleteOptions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && selectedIndex < autocompleteOptions.length) {
        handleSelectOption(autocompleteOptions[selectedIndex]);
      } else {
        handleSearchSubmit(e);
      }
    } else if (e.key === 'Escape') {
      setShowAutocomplete(false);
      setSelectedIndex(-1);
    }
  };

  // Load all countries data on component mount
  useEffect(() => {
    loadCountriesData();
  }, []);

  const loadCountriesData = async () => {
    console.log('🚀 loadCountriesData called');
    setLoadingFramework(true);
    try {
      const data = await FrameworkViz.loadAllCountriesData();
      console.log('✅ Data loaded in LandingPage, rows:', data.length);
      setAllCountriesData(data);
      const countries = FrameworkViz.getAvailableCountries(data);
      console.log('🌍 Available countries:', countries);
      setAvailableCountries(countries);
      if (countries.length > 0) {
        setSelectedCountry(countries[0]);
        console.log('✅ Selected first country:', countries[0]);
      }
    } catch (error) {
      console.error('❌ Error in loadCountriesData:', error);
    } finally {
      setLoadingFramework(false);
    }
  };

  const handleCountryClickFromMap = useCallback((country) => {
    console.log('🗺️ Country clicked from map:', country);
    setPendingCountrySelection(country);
  }, []);

  // Render world map when framework is shown and map view is active
  useEffect(() => {
    console.log('🎯 World map useEffect triggered:', {
        showFramework,
        showWorldMap,
        hasMapRef: !!worldMapRef.current,
        dataLength: allCountriesData.length
    });
    
    if (showFramework && showWorldMap && worldMapRef.current && allCountriesData.length > 0) {
        console.log('✅ All conditions met, calling renderWorldMap');
        FrameworkViz.renderWorldMap(worldMapRef.current, allCountriesData, handleCountryClickFromMap);
    } else {
        console.log('⏭️ Skipping world map render, conditions not met');
    }
  }, [showFramework, showWorldMap, allCountriesData, handleCountryClickFromMap]);

  // Handle pending country selection from map clicks
  useEffect(() => {
    if (pendingCountrySelection) {
      console.log('🎯 Processing pending country selection:', pendingCountrySelection);
      setSelectedCountry(pendingCountrySelection);
      setShowWorldMap(false);
      setSelectedSection(null);
      setSectionDetailedData(null);
      setPendingCountrySelection(null);
    }
  }, [pendingCountrySelection]);

  // Load country data when selection changes
  useEffect(() => {
    if (selectedCountry && !showWorldMap) {
      loadCountryFrameworkData();
    }
  }, [selectedCountry, showWorldMap]);

  // FIXED: Render radial chart when data is ready OR when selectedSection changes
  useEffect(() => {
    if (parsedData && averagedScores.length > 0 && radialChartRef.current && !showWorldMap) {
      console.log('🎨 Rendering radial chart from useEffect...', { selectedSection });
      FrameworkViz.renderRadialChart(
        radialChartRef.current,
        averagedScores,
        FrameworkViz.section_names
      );
    }
  }, [parsedData, averagedScores, showWorldMap, selectedSection]);

  // Event listeners for chart interactions
  useEffect(() => {
    const handleSectionClick = (event) => {
      const section = event.detail.section;
      console.log('📊 Section clicked event received:', section);
      setSelectedSection(section);
      
      if (countryData.length > 0) {
        const detailedData = FrameworkViz.getSectionDetailedData(section, countryData);
        console.log('📝 Detailed data for section:', detailedData);
        setSectionDetailedData(detailedData);
      }
    };

    document.addEventListener('sectionClick', handleSectionClick);
    return () => {
      document.removeEventListener('sectionClick', handleSectionClick);
    };
  }, [countryData]);

  // Render distribution chart when section is selected
  useEffect(() => {
    if (selectedSection && parsedData && distributionChartRef.current) {
      const sectionData = FrameworkViz.getSectionData(selectedSection, parsedData);
      if (sectionData) {
        FrameworkViz.renderDistributionChart(
          distributionChartRef.current,
          selectedSection,
          sectionData.scores,
          sectionData.sub_elements
        );
      }
    }
  }, [selectedSection, parsedData]);

  const loadCountryFrameworkData = async () => {
    if (!selectedCountry) return;

    console.log('📥 Loading framework data for:', selectedCountry);
    setLoadingFramework(true);
    try {
      const filteredCountryData = FrameworkViz.filterDataByCountry(allCountriesData, selectedCountry);
      console.log('📊 Country data rows:', filteredCountryData.length);
      setCountryData(filteredCountryData);
      
      const parsed = FrameworkViz.parseFrameworkData(filteredCountryData);
      console.log('✅ Parsed data:', parsed);
      setParsedData(parsed);

      const averaged = FrameworkViz.getAveragedNormalizedData(
        parsed.scores,
        parsed.sub_elements
      );
      console.log('📈 Averaged scores:', averaged);
      setAveragedScores(averaged);
    } catch (error) {
      console.error('❌ Error loading country framework data:', error);
    } finally {
      setLoadingFramework(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim() && onSearch) {
      onSearch(searchQuery);
    }
    setShowNCCP(false); setShowOECDExplorer(true);
    setShowHVHS(false);
    setShowFramework(false);
  };

  const handleBackToMap = () => {
    setShowWorldMap(true);
    setSelectedSection(null);
    setSectionDetailedData(null);
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#ffffff',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header Navigation */}
      <div style={{
        backgroundColor: 'white',
        borderBottom: '2px solid #e5e7eb',
        padding: '0.5rem 2rem',
        position: 'relative',
        zIndex: 100
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setShowFramework(false);
                setShowHVHS(false);
                setShowOECDExplorer(false);
                setShowWorldMap(true);
                setShowNCCP(false);
              }}
              style={{ display: 'block' }}
            >
              <img
                src="/logo.png"
                alt="Harvard Health Systems Innovation Logo"
                style={{ height: '45px', width: 'auto', display: 'block' }}
              />
            </a>
          </div>
          
          {/* Navigation - Resources tab removed */}
          <nav style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
            {/* TAB 1: Health Systems Performance */}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setShowNCCP(false); setShowOECDExplorer(true);
                setShowHVHS(false);
                setShowFramework(false);
              }}
              style={{
                color: showOECDExplorer ? '#113743' : '#1F2937',
                textDecoration: 'none',
                fontSize: '0.95rem',
                fontWeight: '500',
                padding: '0.5rem 0',
                borderBottom: showOECDExplorer ? '2px solid #113743' : '2px solid transparent'
              }}
            >
              Health Systems Performance
            </a>

            {/* TAB 2: Framework Analysis — HIDDEN (FA1: feature-flagged, not deleted) */}
            {/*
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setShowFramework(true);
                setShowHVHS(false);
                setShowWorldMap(true);
              }}
              style={{
                color: showFramework && !showHVHS ? '#113743' : '#1F2937',
                textDecoration: 'none',
                fontSize: '0.95rem',
                fontWeight: '500',
                padding: '0.5rem 0'
              }}
            >
              Framework Analysis
            </a>
            */}

            {/* TAB: NCCP v2 — PRISM v2 / NCCP V2 demo app, rendered in-page via
                iframe to /nccp/ (dev-server proxy → local Flask service on 5050,
                basic-auth protected). Same look & behaviour as the other tabs. */}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setShowNCCP(true);
                setShowOECDExplorer(false);
                setShowHVHS(false);
                setShowFramework(false);
              }}
              style={{
                color: showNCCP ? '#113743' : '#1F2937',
                textDecoration: 'none',
                fontSize: '0.95rem',
                fontWeight: '500',
                padding: '0.5rem 0',
                borderBottom: showNCCP ? '2px solid #113743' : '2px solid transparent'
              }}
            >
              NCCP v2
            </a>

            {/* TAB 3: High Value Health Systems (Dropdown) */}
            <div ref={hvhsDropdownRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setShowHVHSDropdown(!showHVHSDropdown)}
                style={{
                  color: showHVHS ? '#113743' : '#1F2937',
                  textDecoration: 'none',
                  fontSize: '0.95rem',
                  fontWeight: '500',
                  padding: '0.5rem 0',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  fontFamily: 'inherit'
                }}
              >
                High Value Health Systems
                <span style={{ fontSize: '0.6rem', marginTop: '1px' }}>&#9660;</span>
              </button>

              {showHVHSDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '0.5rem',
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
                  minWidth: '340px',
                  zIndex: 1000,
                  padding: '0.5rem 0',
                  overflow: 'hidden'
                }}>
                  {/* Overall Map View */}
                  <button onClick={() => { setShowNCCP(false); setShowFramework(false); setShowOECDExplorer(false); setShowHVHS(true); setHvhsView('map'); setShowHVHSDropdown(false); }}
                    style={{ width: '100%', textAlign: 'left', padding: '0.75rem 1.25rem', border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <i className="fas fa-globe" style={{ fontSize: '1rem', color: '#113743', width: '20px', textAlign: 'center' }}></i>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#1F2937' }}>Overall Map View</div>
                      <div style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>Global map of high value health system scores</div>
                    </div>
                  </button>

                  {/* Country Profiles */}
                  <button onClick={() => { setShowNCCP(false); setShowFramework(false); setShowOECDExplorer(false); setShowHVHS(true); setHvhsView('profile'); setShowHVHSDropdown(false); }}
                    style={{ width: '100%', textAlign: 'left', padding: '0.75rem 1.25rem', border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <i className="fas fa-chart-pie" style={{ fontSize: '1rem', color: '#113743', width: '20px', textAlign: 'center' }}></i>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#1F2937' }}>Country Profiles</div>
                      <div style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>Radial plot &amp; country detail view</div>
                    </div>
                  </button>

                  {/* Benchmarking Matrix */}
                  <button onClick={() => { setShowNCCP(false); setShowFramework(false); setShowOECDExplorer(false); setShowHVHS(true); setHvhsView('benchmarking'); setShowHVHSDropdown(false); }}
                    style={{ width: '100%', textAlign: 'left', padding: '0.75rem 1.25rem', border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <i className="fas fa-th" style={{ fontSize: '1rem', color: '#113743', width: '20px', textAlign: 'center' }}></i>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#1F2937' }}>Benchmarking Matrix</div>
                      <div style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>Scatter plot benchmarking analysis</div>
                    </div>
                  </button>

                  {/* Divider — separates main HVHS views from "About the Project".
                      Further Insights sub-group removed per CR feedback 27 Apr; that
                      content already lives under "About the Project". */}
                  <div style={{ height: '1px', backgroundColor: '#e5e7eb', margin: '0.5rem 0' }} />

                  {/* About the Project */}
                  <button onClick={() => { setShowNCCP(false); setShowFramework(false); setShowOECDExplorer(false); setShowHVHS(true); setHvhsView('about'); setShowHVHSDropdown(false); }}
                    style={{ width: '100%', textAlign: 'left', padding: '0.75rem 1.25rem', border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <i className="fas fa-info-circle" style={{ fontSize: '1rem', color: '#113743', width: '20px', textAlign: 'center' }}></i>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#1F2937' }}>About the Project</div>
                      <div style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>Project description &amp; methodology</div>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, padding: (showOECDExplorer || showNCCP) ? '0' : '2rem' }}>
        {showNCCP ? (
          // STATE NCCP: PRISM v2 / NCCP V2 demo app, served same-origin at /nccp/
          // (dev-server proxy → local Flask service; basic-auth prompt on first load)
          <iframe
            src="/nccp/"
            title="NCCP v2"
            style={{ width: '100%', height: 'calc(100vh - 75px)', border: 'none', display: 'block' }}
          />
        ) : showOECDExplorer ? (
          // STATE 0: Show Health Systems Performance (OECDExplorer) with persistent top nav
          <OECDExplorer skipLanding={true} />
        ) : showHVHS ? (
          // STATE 1: Show High Value Health Systems Page
          <HVHSPage initialView={hvhsView} />
        ) : !showFramework ? (
          // STATE 2: Show "Home" / Search Page
          <div style={{
            maxWidth: '1400px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '2rem'
          }}>
            {/* Hero Section — Left/Right Layout matching CW_VIZ */}
            <div style={{
              backgroundColor: 'white',
              padding: '3rem 2rem',
              borderRadius: '12px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                display: 'flex',
                gap: '3rem',
                alignItems: 'center'
              }}>
                {/* Left Panel: Title + Search */}
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <h1 style={{
                    fontSize: '2.5rem',
                    fontWeight: '700',
                    color: '#1F2937',
                    margin: 0,
                    textAlign: 'left'
                  }}>
                    High Value Health Systems Visualizer
                  </h1>
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    margin: '1rem 0 0 0'
                  }}>
                    <p style={{
                      fontSize: '1.125rem',
                      color: '#6B7280',
                      margin: 0,
                      lineHeight: '1.6',
                      textAlign: 'left'
                    }}>
                      Compare overall or component level high value health system scores across countries on a global map. Higher scores indicate a stronger transition towards a high value health system.
                    </p>
                    <span style={{ display: 'inline-flex', alignItems: 'center', height: '1.8rem' }}>
                      <FrameworkInfoButton />
                    </span>
                  </div>
                </div>

                {/* Right Panel: Search bar */}
                <div style={{ flex: '0 0 500px', minWidth: '500px' }}>
                  <form onSubmit={handleSearchSubmit} style={{
                    display: 'flex',
                    flexDirection: 'row',
                    position: 'relative'
                  }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onFocus={() => {
                          if (autocompleteOptions.length > 0) {
                            setShowAutocomplete(true);
                          }
                        }}
                        placeholder="Search health indicators..."
                        style={{
                          width: '100%',
                          padding: '0.75rem 1rem',
                          fontSize: '1rem',
                          border: '2px solid #e5e7eb',
                          borderRadius: '8px 0 0 8px',
                          outline: 'none',
                          boxSizing: 'border-box',
                          lineHeight: '1.5'
                        }}
                        onFocusCapture={(e) => e.target.style.borderColor = '#113743'}
                        onBlur={(e) => {
                          setTimeout(() => {
                            e.target.style.borderColor = '#e5e7eb';
                          }, 200);
                        }}
                      />

                      {showAutocomplete && autocompleteOptions.length > 0 && (
                        <div
                          ref={autocompleteRef}
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '2px solid #113743',
                            borderTop: 'none',
                            borderRadius: '0 0 0 8px',
                            maxHeight: '300px',
                            overflowY: 'auto',
                            zIndex: 1000,
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                          }}
                        >
                          {autocompleteOptions.map((option, index) => (
                            <div
                              key={index}
                              onClick={() => handleSelectOption(option)}
                              style={{
                                padding: '0.75rem 1rem',
                                cursor: 'pointer',
                                backgroundColor: index === selectedIndex ? '#EFF6FF' : 'white',
                                borderBottom: index < autocompleteOptions.length - 1 ? '1px solid #e5e7eb' : 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}
                              onMouseEnter={(e) => {
                                if (index !== selectedIndex) {
                                  e.currentTarget.style.backgroundColor = '#F9FAFB';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (index !== selectedIndex) {
                                  e.currentTarget.style.backgroundColor = 'white';
                                }
                              }}
                            >
                              <span style={{
                                fontSize: '0.75rem',
                                padding: '0.2rem 0.5rem',
                                borderRadius: '4px',
                                backgroundColor: option.type === 'variable' ? '#DBEAFE' : '#FEF3C7',
                                color: option.type === 'variable' ? '#1E40AF' : '#92400E',
                                fontWeight: '600'
                              }}>
                                {option.type === 'variable' ? 'Variable' : 'Country'}
                              </span>
                              <span style={{ fontSize: '0.95rem', color: '#1F2937' }}>
                                {option.value}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <button
                      type="submit"
                      style={{
                        padding: '0.75rem 2rem',
                        backgroundColor: '#113743',
                        color: 'white',
                        border: '2px solid #113743',
                        borderRadius: '0 8px 8px 0',
                        fontSize: '1rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s, border-color 0.2s',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxSizing: 'border-box',
                        lineHeight: '1.5',
                        overflow: 'hidden'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1a5c6b'; e.currentTarget.style.borderColor = '#1a5c6b'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#113743'; e.currentTarget.style.borderColor = '#113743'; }}
                    >
                      Search
                    </button>
                  </form>
                </div>
              </div>
            </div>

            {/* Interactive Cards Row — CW_VIZ style */}
            <div style={{ display: 'flex', alignItems: 'stretch', gap: '2rem', width: '100%' }}>
              {/* Card 1: Health Systems Performance */}
              <div
                onClick={openOECDExplorerFromCard}
                style={landingCardStyles.card}
                onMouseEnter={liftLandingCard}
                onMouseLeave={restLandingCard}
              >
                <div style={landingCardStyles.header}>
                  <h3 style={landingCardStyles.title}>
                    Health Systems Visualizer
                  </h3>
                  <p style={landingCardStyles.description}>
                    Compare health-system performance and outcomes.
                  </p>
                </div>
                <div style={landingCardStyles.mapWrap}>
                  <AnimatedWorldMap />
                </div>
                <div style={landingCardStyles.cta}>
                  <button
                    type="button"
                    aria-label="Explore the Health Systems Visualizer"
                    onClick={(e) => { e.stopPropagation(); openOECDExplorerFromCard(); }}
                    style={landingCardStyles.ctaButton}
                  >
                    <span>Explore Health Data</span>
                    <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#113743" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Card 2: High Value Health Systems — HVHS Overall Score world map preview
                  (replaced India 2025 radial per CR feedback 27 Apr) */}
              <div
                onClick={openHVHSFromCard}
                style={landingCardStyles.card}
                onMouseEnter={liftLandingCard}
                onMouseLeave={restLandingCard}
              >
                <div style={landingCardStyles.header}>
                  <h3 style={landingCardStyles.title}>
                    High Value Health Systems
                  </h3>
                  <p style={landingCardStyles.description}>
                    Explore high value health system scores, country profiles, and benchmarks.
                  </p>
                </div>
                <div style={landingCardStyles.mapWrap}>
                  <div style={{ position: 'relative' }}>
                    <HVHSMapPreview />
                    <div style={{ position: 'absolute', inset: 0, cursor: 'pointer' }} />
                  </div>
                </div>
                <div style={landingCardStyles.cta}>
                  <button
                    type="button"
                    aria-label="Explore High Value Health Systems"
                    onClick={(e) => { e.stopPropagation(); openHVHSFromCard(); }}
                    style={landingCardStyles.ctaButton}
                  >
                    <span>Explore High Value Health Systems</span>
                    <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#113743" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            
          </div>
        ) : (
          // STATE 3: Show Framework Analysis Section
          <div style={{
            maxWidth: '1400px',
            margin: '0 auto'
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem'
              }}>
                <h2 style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  color: '#1F2937',
                  margin: 0
                }}>
                  Framework Analysis
                </h2>
                
                {!showWorldMap && (
                  <button
                    onClick={handleBackToMap}
                    style={{
                      padding: '0.625rem 1.25rem',
                      backgroundColor: '#6B7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.9375rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#4B5563'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#6B7280'}
                  >
                    ← Back to World Map
                  </button>
                )}
              </div>

              {loadingFramework && (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  color: '#6B7280'
                }}>
                  Loading framework data...
                </div>
              )}

              {!loadingFramework && showWorldMap && (
                <div style={{
                  backgroundColor: '#f9fafb',
                  padding: '1.5rem',
                  borderRadius: '8px'
                }}>
                  <p style={{
                    fontSize: '1rem',
                    color: '#6B7280',
                    marginBottom: '1.5rem',
                    textAlign: 'center'
                  }}>
                    Click on any country to view its detailed framework analysis
                  </p>
                  {allCountriesData.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '3rem',
                      color: '#EF4444'
                    }}>
                      No data loaded. Check console for errors.
                    </div>
                  ) : (
                    <div 
                      ref={worldMapRef}
                      id="world-map"
                      style={{
                        width: '100%',
                        backgroundColor: 'white',
                        borderRadius: '6px',
                        padding: '1rem'
                      }}
                    />
                  )}
                </div>
              )}

              {!loadingFramework && !showWorldMap && parsedData && (
                <>
                  <div style={{
                    marginBottom: '2rem'
                  }}>
                    <label style={{
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Select Country
                    </label>
                    <select
                      value={selectedCountry}
                      onChange={(e) => setSelectedCountry(e.target.value)}
                      style={{
                        width: '100%',
                        maxWidth: '400px',
                        padding: '0.625rem',
                        fontSize: '1rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        backgroundColor: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      {availableCountries.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: selectedSection ? '1fr 1fr' : '1fr',
                    gap: '2rem',
                    marginBottom: '2rem'
                  }}>
                    <div style={{
                      backgroundColor: '#f9fafb',
                      padding: '1.5rem',
                      borderRadius: '8px'
                    }}>
                      <h3 style={{
                        fontSize: '1.25rem',
                        fontWeight: '600',
                        color: '#1F2937',
                        marginBottom: '1rem',
                        margin: '0 0 1rem 0'
                      }}>
                        Framework Overview - {selectedCountry}
                      </h3>
                      <p style={{
                        fontSize: '0.875rem',
                        color: '#6B7280',
                        marginBottom: '1rem'
                      }}>
                        Click on any section to view detailed metrics
                      </p>
                      <div 
                        ref={radialChartRef}
                        id="radial-chart"
                        style={{
                          width: '100%',
                          height: selectedSection ? '500px' : '600px',
                          backgroundColor: 'white',
                          borderRadius: '6px',
                          padding: '1rem'
                        }}
                      />
                    </div>

                    {selectedSection && sectionDetailedData && (
                      <div style={{
                        backgroundColor: '#f9fafb',
                        padding: '1.5rem',
                        borderRadius: '8px',
                        maxHeight: '700px',
                        overflowY: 'auto'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '1rem',
                          position: 'sticky',
                          top: 0,
                          backgroundColor: '#f9fafb',
                          paddingBottom: '0.5rem',
                          zIndex: 10
                        }}>
                          <h3 style={{
                            fontSize: '1.25rem',
                            fontWeight: '600',
                            color: '#1F2937',
                            margin: 0
                          }}>
                            {selectedSection} - Details
                          </h3>
                          <button
                            onClick={() => {
                              setSelectedSection(null);
                              setSectionDetailedData(null);
                            }}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: '#EF4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              fontWeight: '500'
                            }}
                          >
                            Close
                          </button>
                        </div>
                        
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '1rem'
                        }}>
                          {sectionDetailedData.map((item, index) => (
                            <div
                              key={index}
                              style={{
                                backgroundColor: 'white',
                                borderRadius: '6px',
                                padding: '1rem',
                                border: '1px solid #E5E7EB'
                              }}
                            >
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '0.75rem',
                                paddingBottom: '0.75rem',
                                borderBottom: '2px solid #E5E7EB'
                              }}>
                                <h4 style={{
                                  fontSize: '0.9375rem',
                                  fontWeight: '600',
                                  color: '#1F2937',
                                  margin: 0,
                                  flex: 1
                                }}>
                                  {item.subElement}
                                </h4>
                                <div style={{
                                  display: 'flex',
                                  gap: '0.75rem',
                                  alignItems: 'center'
                                }}>
                                  <span style={{
                                    fontSize: '0.8125rem',
                                    color: '#6B7280'
                                  }}>
                                    {item.displayScore} / {item.maxScore}
                                  </span>
                                  <span style={{
                                    display: 'inline-block',
                                    padding: '0.25rem 0.5rem',
                                    backgroundColor: item.normalizedScore >= 3.5 ? '#D1FAE5' : item.normalizedScore >= 2 ? '#FEF3C7' : '#FEE2E2',
                                    color: item.normalizedScore >= 3.5 ? '#065F46' : item.normalizedScore >= 2 ? '#92400E' : '#991B1B',
                                    borderRadius: '4px',
                                    fontWeight: '600',
                                    fontSize: '0.8125rem'
                                  }}>
                                    {item.normalizedScore.toFixed(1)} / 5.0
                                  </span>
                                </div>
                              </div>

                              <div style={{ marginBottom: '0.75rem' }}>
                                <p style={{
                                  fontSize: '0.8125rem',
                                  fontWeight: '600',
                                  color: '#374151',
                                  marginBottom: '0.25rem',
                                  margin: '0 0 0.25rem 0'
                                }}>
                                  Response:
                                </p>
                                <p style={{
                                  fontSize: '0.8125rem',
                                  color: '#6B7280',
                                  lineHeight: '1.6',
                                  margin: 0
                                }}>
                                  {item.response}
                                </p>
                              </div>

                              <div>
                                <p style={{
                                  fontSize: '0.8125rem',
                                  fontWeight: '600',
                                  color: '#374151',
                                  marginBottom: '0.25rem',
                                  margin: '0 0 0.25rem 0'
                                }}>
                                  Reasoning:
                                </p>
                                <p style={{
                                  fontSize: '0.8125rem',
                                  color: '#6B7280',
                                  lineHeight: '1.6',
                                  margin: 0
                                }}>
                                  {item.reasoning}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {selectedSection && (
                    <div style={{
                      backgroundColor: '#f9fafb',
                      padding: '1.5rem',
                      borderRadius: '8px',
                      marginTop: '2rem'
                    }}>
                      <h3 style={{
                        fontSize: '1.25rem',
                        fontWeight: '600',
                        color: '#1F2937',
                        marginBottom: '1rem',
                        margin: '0 0 1rem 0'
                      }}>
                        {selectedSection} - Score Distribution
                      </h3>
                      <div 
                        ref={distributionChartRef}
                        id="distribution-chart"
                        style={{
                          width: '100%',
                          height: '500px',
                          backgroundColor: 'white',
                          borderRadius: '6px',
                          padding: '1rem'
                        }}
                      />
                    </div>
                  )}

                  <div style={{
                    marginTop: '2rem',
                    padding: '1.5rem',
                    backgroundColor: '#EFF6FF',
                    borderRadius: '8px',
                    border: '1px solid #BFDBFE'
                  }}>
                    <h3 style={{
                      fontSize: '1.125rem',
                      fontWeight: '600',
                      color: '#1F2937',
                      marginBottom: '1rem',
                      margin: '0 0 1rem 0'
                    }}>
                      Framework Definitions
                    </h3>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                      gap: '1rem'
                    }}>
                      {FrameworkViz.section_names.map(section => (
                        <div
                          key={section}
                          style={{
                            padding: '1rem',
                            backgroundColor: 'white',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            border: selectedSection === section ? '2px solid #113743' : '1px solid #E5E7EB'
                          }}
                          onClick={() => {
                            setSelectedSection(section);
                            if (countryData.length > 0) {
                              const detailedData = FrameworkViz.getSectionDetailedData(section, countryData);
                              setSectionDetailedData(detailedData);
                            }
                          }}
                          onMouseEnter={(e) => {
                            if (selectedSection !== section) {
                              e.currentTarget.style.borderColor = '#113743';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(59, 130, 246, 0.1)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (selectedSection !== section) {
                              e.currentTarget.style.borderColor = '#E5E7EB';
                              e.currentTarget.style.boxShadow = '';
                            }
                          }}
                        >
                          <h4 style={{
                            fontSize: '0.9375rem',
                            fontWeight: '600',
                            color: '#1F2937',
                            marginBottom: '0.5rem',  
                            margin: '0 0 0.5rem 0'
                          }}>
                            {section}
                          </h4>
                          <p style={{
                            fontSize: '0.875rem',
                            color: '#6B7280',
                            margin: 0,
                            lineHeight: '1.5'
                          }}>
                            {FrameworkViz.FRAMEWORK_DEFINITIONS[section]}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        backgroundColor: 'white',
        borderTop: '1px solid #e5e7eb',
        padding: '1.5rem 2rem',
        marginTop: 'auto'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <p style={{
            fontSize: '0.875rem',
            color: '#6B7280',
            margin: 0
          }}>
            © Health Systems Innovation Lab, Harvard University, 2025
          </p>
        </div>
      </div>

      {/* Metadata Popup */}
      {showMetadata && selectedVariableMetadata && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem'
          }}
          onClick={() => {
            setShowMetadata(false);
            setSelectedVariableMetadata(null);
          }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '800px',
            maxHeight: '80vh',
            width: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}
          onClick={(e) => e.stopPropagation()}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#1F2937'
              }}>
                Variable Metadata
              </h3>
              <button
                onClick={() => {
                  setShowMetadata(false);
                  setSelectedVariableMetadata(null);
                }}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#6B7280',
                  padding: '0.25rem'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{
              flex: 1,
              overflow: 'auto',
              border: '1px solid #E5E7EB',
              borderRadius: '4px',
              padding: '1rem',
              backgroundColor: '#F9FAFB'
            }}>
              <div style={{ display: 'grid', gap: '1rem' }}>
                {[
                  { key: 'Source', label: 'Source' },
                  { key: 'Component', label: 'Component' },
                  { key: 'Sub-component', label: 'Sub-component' },
                  { key: 'Variable', label: 'Variable' },
                  { key: 'Variable Definition', label: 'Variable Definition' },
                  { key: 'Measure', label: 'Unit of Measure' },
                  { key: 'Unit Standardization', label: 'Unit Standardization' },
                  { key: 'Date Range', label: 'Date Range' },
                  { key: 'URL', label: 'URL' }
                ].map(({ key, label }) => (
                  <div key={key} style={{ borderBottom: '1px solid #E5E7EB', paddingBottom: '0.75rem' }}>
                    <dt style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>
                      {label}:
                    </dt>
                    <dd style={{ fontSize: '0.875rem', color: '#1F2937', margin: 0, wordBreak: 'break-word' }}>
                      {key === 'URL' && selectedVariableMetadata[key] ? (
                        <a 
                          href={selectedVariableMetadata[key]} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          style={{ color: '#113743', textDecoration: 'underline' }}
                        >
                          {selectedVariableMetadata[key]}
                        </a>
                      ) : (
                        selectedVariableMetadata[key] || 'Not specified'
                      )}
                    </dd>
                  </div>
                ))}
              </div>
            </div>
            
            <div style={{
              marginTop: '1.5rem',
              textAlign: 'right'
            }}>
              <button
                onClick={() => {
                  setShowMetadata(false);
                  setSelectedVariableMetadata(null);
                }}
                style={{
                  backgroundColor: '#113743',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;